import moment from "moment";
import { useT } from "@hooks";
import { resolvePaymentMethodLabel } from "@utils/paymentMethodLabel";
import type { MaintenanceTransaction } from "@services/maintenance";

interface MaintenanceDetailProps {
  transaction: MaintenanceTransaction;
}

interface DetailField {
  id: string;
  label: string;
  value: string;
  addon?: string;
}

const formatAmount = (value: number) =>
  value.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatDateTime = (value: string) => {
  const dateTime = moment(value);
  return dateTime.isValid() ? dateTime.format("DD/MM/YYYY HH:mm") : "";
};

export default function MaintenanceDetail({ transaction }: Readonly<MaintenanceDetailProps>) {
  const t = useT();

  const fields: DetailField[] = [
    {
      id: "maintenanceDetailDateTime",
      label: t("maintenance.table.column.dateTime"),
      value: formatDateTime(transaction.dateTime),
    },
    { id: "maintenanceDetailVehicle", label: t("maintenance.table.column.vehicle"), value: transaction.vehicle },
    {
      id: "maintenanceDetailType",
      label: t("maintenance.table.column.type"),
      value: t(`maintenance.type.${transaction.type}`),
    },
    {
      id: "maintenanceDetailSubcategory",
      label: t("maintenance.table.column.subcategory"),
      value: transaction.subcategory,
    },
    {
      id: "maintenanceDetailPaymentMethod",
      label: t("maintenance.table.column.paymentMethod"),
      value: resolvePaymentMethodLabel(transaction.paymentMethod, t) ?? "",
    },
    {
      id: "maintenanceDetailAmount",
      label: t("maintenance.table.column.amount"),
      value: formatAmount(transaction.amount),
      addon: "R$",
    },
    {
      id: "maintenanceDetailStatus",
      label: t("maintenance.table.column.status"),
      value: t(`maintenance.status.${transaction.status}`),
    },
  ];

  return (
    <div className="padding-x-20">
      <form>
        <fieldset>
          {fields.map((field) => {
            const input = (
              <input id={field.id} type="text" className="form-control" readOnly value={field.value || "-"} />
            );

            return (
              <div key={field.id} className="form-group">
                <label htmlFor={field.id}>{field.label}</label>
                {field.addon ? (
                  <div className="input-group">
                    <span className="input-group-addon">{field.addon}</span>
                    {input}
                  </div>
                ) : (
                  input
                )}
              </div>
            );
          })}
        </fieldset>
      </form>
    </div>
  );
}
